import { useCallback, useEffect, useRef, useState } from "react"
import { Capacitor } from "@capacitor/core"
import { App } from "@capacitor/app"
import { Preferences } from "@capacitor/preferences"
import {
    AppUpdate,
    AppUpdateAvailability
} from "@capawesome/capacitor-app-update"
import { isPreferencesAvailable } from "@/utils/capacitorPreferences"
import { ANDROID_PACKAGE_NAME, IOS_APP_STORE_ID } from "@/constants/appStore"

const SUPPRESS_KEY = "app_update_suppressed_at"
const CHECK_COOLDOWN_MS = 4 * 60 * 60 * 1000 // 4時間
const DEFAULT_SUPPRESS_MS = 24 * 60 * 60 * 1000 // 24時間

const getSuppressDuration = (): number => {
    const override = Number(process.env.APP_UPDATE_SUPPRESS_MS)
    return Number.isFinite(override) && override > 0
        ? override
        : DEFAULT_SUPPRESS_MS
}

const readSuppressedAt = async (): Promise<number | null> => {
    if (!isPreferencesAvailable()) return null
    const { value } = await Preferences.get({ key: SUPPRESS_KEY })
    if (!value) return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const writeSuppressedAt = async (timestamp: number): Promise<void> => {
    if (!isPreferencesAvailable()) return
    await Preferences.set({ key: SUPPRESS_KEY, value: String(timestamp) })
}

export const useAppUpdateCheck = () => {
    const [open, setOpen] = useState(false)
    const [availableVersion, setAvailableVersion] = useState<
        string | undefined
    >(undefined)
    const lastCheckedAtRef = useRef(0)
    const isCheckingRef = useRef(false)

    const check = useCallback(async () => {
        if (!Capacitor.isNativePlatform()) return
        if (isCheckingRef.current) return
        if (Date.now() - lastCheckedAtRef.current < CHECK_COOLDOWN_MS) return

        isCheckingRef.current = true
        lastCheckedAtRef.current = Date.now()
        try {
            const info = await AppUpdate.getAppUpdateInfo()

            if (
                info.updateAvailability !==
                AppUpdateAvailability.UPDATE_AVAILABLE
            ) {
                return
            }

            const suppressedAt = await readSuppressedAt()
            if (
                suppressedAt &&
                Date.now() - suppressedAt < getSuppressDuration()
            ) {
                return
            }

            setAvailableVersion(info.availableVersionName)
            setOpen(true)
        } catch (error) {
            console.error("[AppUpdateCheck] Failed to check app update:", error)
        } finally {
            isCheckingRef.current = false
        }
    }, [])

    const handleUpdate = useCallback(async () => {
        try {
            if (Capacitor.getPlatform() === "ios") {
                await AppUpdate.openAppStore({ appId: IOS_APP_STORE_ID })
            } else {
                await AppUpdate.openAppStore({
                    androidPackageName: ANDROID_PACKAGE_NAME
                })
            }
            await writeSuppressedAt(Date.now())
            setOpen(false)
        } catch (error) {
            console.error("[AppUpdateCheck] Failed to open app store:", error)
        }
    }, [])

    const handleLater = useCallback(async () => {
        try {
            await writeSuppressedAt(Date.now())
        } catch (error) {
            console.error("[AppUpdateCheck] Failed to save suppression:", error)
        }
        setOpen(false)
    }, [])

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return

        // 初期チェックはマイクロタスクで遅延発火し、effect本体での同期setStateを避ける
        // アンマウント時はフラグで発火を抑止（マイクロタスク自体はキャンセル不可のため）
        let cancelled = false
        queueMicrotask(() => {
            if (cancelled) return
            void check()
        })

        const listener = App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) void check()
        })

        return () => {
            cancelled = true
            listener.then(l => l.remove())
        }
    }, [check])

    return {
        open,
        availableVersion,
        onUpdate: handleUpdate,
        onLater: handleLater
    }
}
